import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { listCurrencies, deleteCurrency } from '../api/currencyApi'
import { appConfirm } from '../../../../components/AppConfirmModal'

export function useCurrencyListScreen() {
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMutating, setIsMutating] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [reloadTick, setReloadTick] = useState(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError('')

    listCurrencies()
      .then((data) => {
        if (!active) return
        setItems(data)
      })
      .catch((err) => {
        if (!active) return
        setError(extractApiErrorMessage(err, 'Failed to load currencies.'))
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [reloadTick])

  const rows = useMemo(() => {
    let filtered = items

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter((c) => c.isActive === 1 || c.is_active === 1)
    } else {
      filtered = filtered.filter((c) => c.isActive !== 1 && c.is_active !== 1)
    }

    // Filter by code or name search
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      filtered = filtered.filter(
        (c) =>
          String(c.currencyCode || c.currency_code || '').toLowerCase().includes(q) ||
          String(c.currencyName || c.currency_name || '').toLowerCase().includes(q),
      )
    }

    return filtered
  }, [items, statusFilter, searchText])

  const totalCount = items.length
  const activeCount = items.filter((c) => c.isActive === 1 || c.is_active === 1).length

  async function handleDelete(currencyCode) {
    const confirmed = await appConfirm({
      title: 'Delete Currency',
      message: `Delete currency ${currencyCode}? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsMutating(true)
    setError('')
    try {
      await deleteCurrency(currencyCode)
      setSuccess(`${currencyCode} deleted successfully.`)
      setReloadTick((t) => t + 1)
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to delete currency. It may be in use by active companies or projects.'))
    } finally {
      setIsMutating(false)
    }
  }

  return {
    state: {
      rows,
      isLoading,
      isMutating,
      error,
      success,
      searchText,
      statusFilter,
      totalCount,
      activeCount,
    },
    actions: {
      setSearchText,
      setStatusFilter,
      handleDelete,
    },
  }
}
